import { useCallback, useMemo, useState } from 'react';

import { groupColumnsByEntity } from '../utils/columnGroups.js';

/** `All` pill: not a column type, so it is kept out of `COLUMN_TYPES`. */
export const ALL_COLUMN_TYPES = 'all';

/** Type pills the modal offers, in the prototype's order. */
export const COLUMN_TYPE_PILLS = Object.freeze([ALL_COLUMN_TYPES, 'text', 'number', 'date']);

/**
 * Draft state for the column picker modal (CT-5195).
 *
 * Nothing reaches `useWidgetBuilderForm` until `Done`: toggling a checkbox
 * writes to the draft only, so `Cancel` and `Esc` genuinely discard and
 * reopening shows the last **committed** selection.
 *
 * The draft is re-seeded from `committedSelection` by comparing a session tag
 * rather than writing it in an effect — same trick as the entity picker, and
 * it keeps the reopen-after-cancel case correct with no extra render. The tag
 * is a generation counter bumped on every false→true `isOpen` transition, not
 * `isOpen` itself: a plain boolean only has two values, so once the modal has
 * been opened more than once a *new* open can land on the same boolean the
 * *previous* one used and wrongly pass off a stale draft as current. That
 * became reachable once `committedSelection` could change while the modal
 * sat closed (a chip's own remove button, not just `Done`) — the very next
 * open needs a session tag the old draft cannot coincidentally match.
 *
 * @param {{ columns: object[], committedSelection: string[], isOpen: boolean }} options
 */
export const useColumnPicker = ({ columns = [], committedSelection = [], isOpen = false } = {}) => {
  // Adjusting state during render (React's own pattern for deriving from a
  // prop change) rather than a ref: bumping a ref on the false→true edge
  // would count as the very "read + write during render" the lint's
  // `react-hooks/refs` rule exists to catch.
  const [wasOpen, setWasOpen] = useState(isOpen);
  const [generation, setGeneration] = useState(0);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setGeneration((current) => current + 1);
  }

  const [draft, setDraft] = useState({ selected: committedSelection, session: generation });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState(ALL_COLUMN_TYPES);

  const draftSelected = draft.session === generation ? draft.selected : committedSelection;

  const commitDraft = useCallback(
    (selected) => setDraft({ selected, session: generation }),
    [generation],
  );

  /**
   * Functional update, not a read of `draftSelected` from the closure: two
   * toggles inside one React batch would otherwise both start from the same
   * pre-click list and the second would overwrite the first.
   */
  const toggle = useCallback(
    (fieldUuid) => {
      setDraft((current) => {
        const base = current.session === generation ? current.selected : committedSelection;
        return {
          session: generation,
          selected: base.includes(fieldUuid)
            ? base.filter((uuid) => uuid !== fieldUuid)
            : [...base, fieldUuid],
        };
      });
    },
    [committedSelection, generation],
  );

  const selectAll = useCallback(
    () => commitDraft(columns.map((column) => column.fieldUuid)),
    [columns, commitDraft],
  );

  const deselectAll = useCallback(() => commitDraft([]), [commitDraft]);

  const resetDraft = useCallback(() => {
    setDraft({ selected: committedSelection, session: generation });
    setSearchTerm('');
    setActiveType(ALL_COLUMN_TYPES);
  }, [committedSelection, generation]);

  const visibleColumns = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return columns.filter((column) => {
      if (activeType !== ALL_COLUMN_TYPES && column.type !== activeType) return false;
      if (!needle) return true;
      // All three, not just the display name: a user who knows the field key or
      // remembers a phrase from the description should find the column.
      return (
        column.displayName.toLowerCase().includes(needle)
        || column.description.toLowerCase().includes(needle)
        || column.internalName.toLowerCase().includes(needle)
      );
    });
  }, [activeType, columns, searchTerm]);

  /** One block per source entity; see `groupColumnsByEntity`. */
  const visibleGroups = useMemo(() => groupColumnsByEntity(visibleColumns), [visibleColumns]);

  return useMemo(
    () => ({
      draftSelected,
      visibleGroups,
      searchTerm,
      activeType,
      visibleColumns,
      selectedCount: draftSelected.length,
      totalCount: columns.length,
      toggle,
      selectAll,
      deselectAll,
      setSearchTerm,
      setActiveType,
      resetDraft,
    }),
    [
      activeType,
      columns.length,
      deselectAll,
      draftSelected,
      resetDraft,
      searchTerm,
      selectAll,
      toggle,
      visibleColumns,
      visibleGroups,
    ],
  );
};

export default useColumnPicker;
